import { Input } from "@hostingbv/lib/src/components/form/Input";
import { FC, useEffect, useState } from "react";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "@hostingbv/lib/src/components/Card";
import { ObjectAutocomplete } from "@hostingbv/lib/src/components/form/Autocomplete";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { useRefresh } from "@hostingbv/lib/src/components/RefreshController";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { DomainPrice } from "@hostingbv/lib/src/api/partner/models/DomainPrice";
import { Loading } from "@hostingbv/lib/src/components/Loading";
import { useContactOptions, useWhoisOptions } from "./DomainDetails";
import { Toggle } from "@hostingbv/lib/src/components/form/Toggle";
import { usePermissions } from "../../../lib/src/auth/permissions";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { faGlobe, faPencilAlt, faShoppingCart, faStar, faWarning } from "@fortawesome/free-solid-svg-icons";
import { PageIconHeader } from "@hostingbv/lib/src/components/PageIconHeader";
import { Contact } from "@hostingbv/lib/src/api/common/models/Contact";
import { Customer } from "@hostingbv/lib/src/api/common/models/Customer";


export const DomainOrder: FC = () => {
  const navigate = useNavigate()
  const refreshOtherPages = useRefresh()
  const { domain, orderType } = useParams()
  const [transferSecret, setTransferSecret] = useState<string>("")
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!

  const { partnerClient } = useApiClient(config)

  const whoisOptions = useWhoisOptions()
  const reseller = useContactOptions('reseller', whoisOptions.customerContacts, whoisOptions.ownCustomerContacts, whoisOptions.useCustomersWhoisContacts)
  const owner = useContactOptions('owner', whoisOptions.customerContacts, whoisOptions.ownCustomerContacts, whoisOptions.useCustomersWhoisContacts)
  const admin = useContactOptions('admin', whoisOptions.customerContacts, whoisOptions.ownCustomerContacts, whoisOptions.useCustomersWhoisContacts)
  const tech = useContactOptions('tech', whoisOptions.customerContacts, whoisOptions.ownCustomerContacts, whoisOptions.useCustomersWhoisContacts)

  const [registerError, setRegisterError] = useState('')
  const missingTransferSecret = orderType === "transfer" && transferSecret === ""
  const missingContacts = owner.selectedOption === null || tech.selectedOption === null || admin.selectedOption === null || reseller.selectedOption === null

  const orderDomain = async function () {
    const response = await partnerClient.createDomain({
      domain: domain,
      type: orderType === "transfer" ? "transfer" : "register",
      transfer_code: orderType === "transfer" ? transferSecret : null,
      domain_owner: owner.selectedOption!.id,
      domain_admin: admin.selectedOption!.id,
      domain_tech: tech.selectedOption!.id,
      domain_reseller: reseller.selectedOption!.id,
    })
    if (!response.success) {
      setRegisterError(response.error)
    } else {
      refreshOtherPages()
      awaitNextDeploymentFinished(response.data.entityId!).then(() => {
        refreshOtherPages()
      })
      navigate(`/domains/${response.data.entityId!}`)
    }
  }
  const [domainPrice, setDomainPrice] = useState<DomainPrice | null>(null)
  const { can } = usePermissions(partnerClient)
  useEffect(() => {
    if (domainPrice?.domain !== domain && domain && can('view', 'contracts')) {
      partnerClient.getDomainPrice(domain).then((price) => {
        if (price.success) {
          setDomainPrice(price.data)
        }
      })
    }
  }, [domain, domainPrice, partnerClient, can])

  if (!['transfer', 'register'].includes(orderType ?? "")) {
    return <div>
      Invalid order type.
    </div>
  }

  return (
    <ContentContainer>
      <PageIconHeader icon={faGlobe}>
        <Breadcrumbs crumbs={[
          { label: "Domains", href: "/domains" },
          { label: "Search domain", href: domain ? `/domains/register/${domain}` : `/domains/register` },
        ]} currentPage={"Complete your order"} />
        <PageHeader>Complete your order</PageHeader>
      </PageIconHeader>
      <Card title={"Domain"}>
        <div className={"flex justify-between"}>
          <strong className={"text-lg"}>{domain}</strong>
          <Button type={"secondary"} size={"xs"} text={"Modify"} icon={faPencilAlt} onClick={() => navigate('/domains/register/')} />
        </div>
        {orderType === "transfer" && <div className={"mt-4"}>
          <Paragraph>
            Because you are transferring your domain, you need to provide the transfer secret.
          </Paragraph>
          <Input type={"text"} label={"Transfer secret"} value={transferSecret} onChange={setTransferSecret} />
        </div>}
      </Card>
      <Card title={"Assign Customer and WHOIS contact"}>
        <Paragraph>
          The reseller contact is a contact on your own customer. You can modify this contact in the customer details.
        </Paragraph>
        <ObjectAutocomplete<Contact> label={"Reseller Contact"} options={reseller.options} value={reseller.selectedOption} onChange={reseller.setSelectedOption} idProperty={"id"} displayItem={(c) => `${c.firstName} ${c.lastName}`} />
        <hr className={"my-4 -mx-6 border-slate-100"} />

        <Paragraph>
          The customer you assign may also get the rights to manage the domain if you give them access.
        </Paragraph>
        <ObjectAutocomplete<Customer> label={"Customer"} options={whoisOptions.customers} value={whoisOptions.customer} onChange={whoisOptions.selectCustomer} displayItem={c => c.displayName} idProperty={'id'} />
        <hr className={"my-4 -mx-6 border-slate-100"} />
        {(owner.options.length === 0 || tech.options.length === 0 || admin.options.length === 0) ? <>
          <Callout color={"warning"} icon={faWarning}>
            <strong>Warning:</strong> This customer has no contacts. You will need to add at least one contact before you can continue.
          </Callout>
        </> : <>
          <div>
            <Toggle value={whoisOptions.useCustomersWhoisContacts} onChange={whoisOptions.setUseCustomersDomainContacts} label={"Use the selected customer's WHOIS contacts"} />
          </div>
          <div className={"mt-4 flex space-x-4"}>
            <div className={"flex-1"}><ObjectAutocomplete<Contact> idProperty={"id"} displayItem={(c) => `${c.firstName} ${c.lastName}`} label={"Owner"} options={owner.options} value={owner.selectedOption} onChange={owner.setSelectedOption} /></div>
            <div className={"flex-1"}><ObjectAutocomplete<Contact> idProperty={"id"} displayItem={(c) => `${c.firstName} ${c.lastName}`} label={"Technical Contact"} options={tech.options} value={tech.selectedOption} onChange={tech.setSelectedOption} /></div>
            <div className={"flex-1"}><ObjectAutocomplete<Contact> idProperty={"id"} displayItem={(c) => `${c.firstName} ${c.lastName}`} label={"Financial Contact"} options={admin.options} value={admin.selectedOption} onChange={admin.setSelectedOption} /></div>
          </div>
        </>}
      </Card>
      <Card title={"Order"}>
        <Paragraph>
          Once you place your order, the domain will be registered or transferred. You will be able to manage the domain in the domain details. The domain will be automatically billed to your account.
        </Paragraph>
        {can('view', 'contracts') ? <Loading loading={!domainPrice}>
          {domainPrice ? <ul className={"text-sm space-y-2 my-4 p-2 text-brand-800 font-medium"}>
            <li>The renewal costs of this domain are € {(domainPrice.price / 100).toFixed(2)} / year (excl. VAT)</li>
            {domainPrice.transferPrice === 0 ? <>
              <li>There are no transfer costs for this domain</li>
            </> : <>
              <li>The transfer costs of this domain are € {(domainPrice.price / 100).toFixed(2)} (excl. VAT)</li>
            </>}
            {domainPrice.isPremium ? <>
              <Callout color={'primary'} icon={faStar}>
                <h2>This is a Premium Domain</h2>
                <p className={"font-normal"}>
                  This means that the price of this domain is higher than the normal price. The price of this domain is € {(domainPrice.price / 100).toFixed(2)} / year (excl. VAT).
                  You cannot order this domain automatically. Please contact us to order this domain.
                </p>
              </Callout>
            </> : <></>}

          </ul> : <></>}
        </Loading> : <></>}

        {missingTransferSecret && <Callout color={"warning"} icon={faWarning}>
          The transfer secret is required before you transfer a domain.
        </Callout>}
        {missingContacts && <Callout color={"warning"} icon={faWarning}>
          Make sure you have assigned all the required contacts. If the customer you have selected does not have any contacts, you will need to add at least one contact before you can continue. You can do this in the customer details.
        </Callout>}
        {registerError !== "" && <Callout color={"danger"} icon={faWarning}>
          <strong>Failed to register domain:</strong> {registerError}
        </Callout>}
        {!domainPrice || (domainPrice && !domainPrice.isPremium) && <>
          <Button type={"primary"} size={"sm"} text={"Place order"} disabled={missingTransferSecret || missingContacts || domainPrice?.isPremium} onClick={orderDomain} icon={faShoppingCart} />
        </>}
      </Card>
    </ContentContainer>
  );
}
